/**
 * 題問加對盤：用問題關鍵字 ∪ 當前盤面標籤，對 nihai-quotes 做客戶端評分導航。
 * 唔係 AI 解盤／唔係 v4 數據集 lookup。
 */
import type { NihaiEntry } from './nihai';
import type { Palace, ZiweiChart } from './engine/ziwei/types';

export type GuideTerm = {
  /** 用嚟同語料匹配嘅字串（多為簡體，同語料一致） */
  term: string;
  /** 粵語短因由（顯示用） */
  reason: string;
  source: 'question' | 'chart';
  weight: number;
};

export type GuideHit = {
  entry: NihaiEntry;
  score: number;
  reasons: string[];
  matchedTerms: string[];
};

/** 繁→簡常見對照（匹配用） */
const TRAD_TO_SIMP: Record<string, string> = {
  宮: '宫',
  財: '财',
  祿: '禄',
  權: '权',
  遷: '迁',
  移: '移',
  僕: '仆',
  華: '华',
  蓋: '盖',
  陽: '阳',
  陰: '阴',
  機: '机',
  門: '门',
  貪: '贪',
  狼: '狼',
  殺: '杀',
  祿存: '禄存',
  體: '体',
  儀: '仪',
  醫: '医',
  學: '学',
  問: '问',
  題: '题',
  業: '业',
  職: '职',
  緣: '缘',
  運: '运',
  歲: '岁',
  對: '对',
  盤: '盘',
  說: '说',
  語: '语',
  與: '与',
  為: '为',
  來: '来',
  個: '个',
  這: '这',
  麼: '么',
  無: '无',
  開: '开',
  關: '关',
  鍵: '键',
  見: '见',
  親: '亲',
  處: '处',
  務: '务',
  會: '会',
  長: '长',
  後: '后',
  時: '时',
  間: '间',
  現: '现',
  發: '发',
  錢: '钱',
  傳: '传',
  統: '统',
  醫藥: '医药',
  術: '术',
  數: '数',
  鬥: '斗',
  紀: '纪',
  書: '书',
  讀: '读',
  講: '讲',
  論: '论',
  觀: '观',
  應: '应',
  該: '该',
  總: '总',
  綱: '纲',
  類: '类',
  護: '护',
  傷: '伤',
  災: '灾',
  難: '难',
  貴: '贵',
  貧: '贫',
  窮: '穷',
  變: '变',
  動: '动',
  靜: '静',
  國: '国',
  區: '区',
  實: '实',
  質: '质',
  際: '际',
  內: '内',
  兩: '两',
  齊: '齐',
  齋: '斋',
  馬: '马',
  鵬: '鹏',
  鳳: '凤',
  龍: '龙',
  鳥: '鸟',
  魚: '鱼',
  祿馬: '禄马',
};

export function toSimplified(s: string): string {
  let out = '';
  for (const ch of s) {
    out += TRAD_TO_SIMP[ch] ?? ch;
  }
  return out;
}

/** 星曜（簡體 canonical） */
const STARS = [
  '紫微', '天机', '太阳', '武曲', '天同', '廉贞', '天府', '太阴', '贪狼', '巨门',
  '天相', '天梁', '七杀', '破军',
  '文昌', '文曲', '左辅', '右弼', '天魁', '天钺', '禄存', '天马',
  '擎羊', '陀罗', '火星', '铃星', '地空', '地劫', '天空',
  '红鸾', '天喜', '孤辰', '寡宿', '华盖', '咸池', '天刑', '天姚',
  '恩光', '天贵', '三台', '八座', '台辅', '封诰', '龙池', '凤阁',
  '天哭', '天虚', '蜚廉', '破碎', '大耗', '旬空', '截空',
];

const PALACES: { simp: string; trad: string; aliases: string[] }[] = [
  { simp: '命宫', trad: '命宮', aliases: ['命盘', '命盤', '本命'] },
  { simp: '兄弟', trad: '兄弟宮', aliases: ['兄弟宫'] },
  { simp: '夫妻', trad: '夫妻宮', aliases: ['夫妻宫', '婚姻', '感情', '配偶'] },
  { simp: '子女', trad: '子女宮', aliases: ['子女宫', '小孩', '孩子'] },
  { simp: '财帛', trad: '財帛宮', aliases: ['财帛宫', '財帛', '财运', '財運', '钱财', '錢財', '发财', '發財'] },
  { simp: '疾厄', trad: '疾厄宮', aliases: ['疾厄宫', '健康', '疾病', '身体', '身體', '病'] },
  { simp: '迁移', trad: '遷移宮', aliases: ['迁移宫', '遷移', '外出', '搬迁', '搬遷', '出国', '出國'] },
  { simp: '仆役', trad: '交友宮', aliases: ['仆役宫', '交友', '朋友', '下属', '下屬'] },
  { simp: '官禄', trad: '官祿宮', aliases: ['官禄宫', '官祿', '事业', '事業', '工作', '职业', '職業', '升职', '升職'] },
  { simp: '田宅', trad: '田宅宮', aliases: ['田宅宫', '房产', '房產', '置业', '置業', '屋企', '家居'] },
  { simp: '福德', trad: '福德宮', aliases: ['福德宫', '精神', '享福', '心态', '心態'] },
  { simp: '父母', trad: '父母宮', aliases: ['父母宫', '长辈', '長輩'] },
];

const SIHUA: { simp: string; aliases: string[]; reason: string }[] = [
  { simp: '化禄', aliases: ['化祿', '禄', '祿'], reason: '你問化祿' },
  { simp: '化权', aliases: ['化權', '权', '權'], reason: '你問化權' },
  { simp: '化科', aliases: ['科'], reason: '你問化科' },
  { simp: '化忌', aliases: ['忌'], reason: '你問化忌' },
  { simp: '四化', aliases: [], reason: '你問四化' },
];

const TOPICS: { simp: string; aliases: string[]; reason: string }[] = [
  { simp: '大限', aliases: ['限'], reason: '你問大限' },
  { simp: '流年', aliases: ['流月', '流日'], reason: '你問流年' },
  { simp: '身宫', aliases: ['身宮'], reason: '你問身宮' },
  { simp: '三方四正', aliases: ['三方', '四正'], reason: '你問三方四正' },
  { simp: '对宫', aliases: ['對宮', '借星'], reason: '你問對宮／借星' },
  { simp: '空宫', aliases: ['空宮', '空'], reason: '你問空宮' },
  { simp: '自化', aliases: [], reason: '你問自化' },
  { simp: '五行', aliases: ['局'], reason: '你問五行局' },
  { simp: '女命', aliases: ['女性', '女人'], reason: '你問女命' },
  { simp: '男命', aliases: ['男性', '男人'], reason: '你問男命' },
  { simp: '阳宅', aliases: ['陽宅', '风水', '風水'], reason: '你問陽宅' },
  { simp: '相法', aliases: ['气色', '氣色', '面相'], reason: '你問相法／氣色' },
  { simp: '中医', aliases: ['中醫', '经脉', '經脈', '脏腑', '臟腑'], reason: '你問中醫／經脈' },
];

const PALACE_LABEL: Record<string, string> = {
  命宫: '命宮',
  兄弟: '兄弟宮',
  夫妻: '夫妻宮',
  子女: '子女宮',
  财帛: '財帛宮',
  疾厄: '疾厄宮',
  迁移: '遷移宮',
  仆役: '交友宮',
  官禄: '官祿宮',
  田宅: '田宅宮',
  福德: '福德宮',
  父母: '父母宮',
};

function pushUnique(list: GuideTerm[], t: GuideTerm): void {
  const exist = list.find((x) => x.term === t.term && x.source === t.source);
  if (exist) {
    if (t.weight > exist.weight) exist.weight = t.weight;
    return;
  }
  list.push(t);
}

/** 由問題文字抽出關鍵字 */
export function extractQuestionTerms(question: string): GuideTerm[] {
  const q = question.trim();
  if (!q) return [];
  const qs = toSimplified(q);
  const out: GuideTerm[] = [];

  for (const star of STARS) {
    if (qs.includes(star) || q.includes(star)) {
      pushUnique(out, { term: star, reason: `你問${star}`, source: 'question', weight: 3 });
    }
  }

  for (const p of PALACES) {
    const hit =
      qs.includes(p.simp) ||
      q.includes(p.trad) ||
      p.aliases.some((a) => qs.includes(toSimplified(a)) || q.includes(a));
    if (hit) {
      pushUnique(out, {
        term: p.simp,
        reason: `你問${p.trad.replace(/宮$/, '') || p.trad}`,
        source: 'question',
        weight: 3,
      });
      // 亦用「XX宫」形態加強 h2 匹配
      pushUnique(out, {
        term: p.simp.endsWith('宫') ? p.simp : `${p.simp}宫`,
        reason: `你問${p.trad}`,
        source: 'question',
        weight: 2.5,
      });
    }
  }

  for (const s of SIHUA) {
    // 避免單獨「禄／权／科／忌」誤傷：要求前面有「化」或完整詞，或問題較短且明確
    const full = s.simp;
    const hasFull = qs.includes(full) || s.aliases.some((a) => a.length >= 2 && (qs.includes(toSimplified(a)) || q.includes(a)));
    const hasHuaShort =
      s.simp.startsWith('化') &&
      (qs.includes(s.simp) ||
        new RegExp(`化[${s.simp.slice(1)}祿權]`).test(qs) ||
        (s.simp === '化忌' && /化忌|逢忌|入忌/.test(qs)) ||
        (s.simp === '化禄' && /化禄|化祿/.test(qs + q)) ||
        (s.simp === '化权' && /化权|化權/.test(qs + q)) ||
        (s.simp === '化科' && /化科/.test(qs)));
    if (hasFull || hasHuaShort || (s.simp === '四化' && /四化/.test(qs))) {
      pushUnique(out, { term: s.simp, reason: s.reason, source: 'question', weight: 3 });
    }
  }

  for (const t of TOPICS) {
    if (qs.includes(t.simp) || t.aliases.some((a) => qs.includes(toSimplified(a)) || q.includes(a))) {
      pushUnique(out, { term: t.simp, reason: t.reason, source: 'question', weight: 2.2 });
    }
  }

  // 若詞庫已命中，唔再抽自由短語（避免「忌點睇婚」類噪音）；否則兜底抽 2–4 字
  const lexiconHits = out.filter((x) => x.weight >= 2);
  if (lexiconHits.length === 0) {
    const chunks = qs.match(/[\u4e00-\u9fff]{2,4}/g) ?? [];
    const stop = new Set([
      '什么', '怎么', '如何', '可以', '一下', '这个', '那个', '自己', '我们', '他们',
      '点睇', '點睇', '点样', '點樣', '理解', '请问', '請問', '想问', '想問',
    ]);
    for (const c of chunks) {
      if (stop.has(c) || STARS.includes(c)) continue;
      pushUnique(out, { term: c, reason: `關鍵字「${c}」`, source: 'question', weight: 1.15 });
    }
  }

  return out;
}

function majorsOf(p: Palace): string[] {
  return p.stars.filter((s) => s.type === 'major').map((s) => s.name);
}

/** 由當前盤面抽出對盤標籤 */
export function extractChartTerms(chart: ZiweiChart): GuideTerm[] {
  const out: GuideTerm[] = [];
  const ming = chart.palaces.find((p) => p.isMingGong);
  const shen = chart.palaces.find((p) => p.isShenGong);

  if (ming) {
    const majors = majorsOf(ming);
    for (const s of majors) {
      pushUnique(out, { term: s, reason: `命宮有${s}`, source: 'chart', weight: 2.2 });
    }
    pushUnique(out, { term: '命宫', reason: '對盤：命宮', source: 'chart', weight: 1.2 });
  }

  if (shen) {
    pushUnique(out, { term: '身宫', reason: '對盤：身宮', source: 'chart', weight: 1.4 });
    for (const s of majorsOf(shen)) {
      pushUnique(out, { term: s, reason: `身宮有${s}`, source: 'chart', weight: 1.6 });
    }
  }

  if (chart.mingZhu) {
    pushUnique(out, { term: chart.mingZhu, reason: `命主${chart.mingZhu}`, source: 'chart', weight: 1.3 });
  }
  if (chart.shenZhu) {
    pushUnique(out, { term: chart.shenZhu, reason: `身主${chart.shenZhu}`, source: 'chart', weight: 1.1 });
  }

  if (chart.wuxingJuName) {
    const ju = toSimplified(chart.wuxingJuName);
    pushUnique(out, { term: ju, reason: `五行局：${chart.wuxingJuName}`, source: 'chart', weight: 1.0 });
    const ele = ju.replace(/[二三四五六]局/, '');
    if (ele) pushUnique(out, { term: ele, reason: `五行：${ele}`, source: 'chart', weight: 0.6 });
  }

  for (const p of chart.palaces) {
    const label = PALACE_LABEL[p.name] ?? p.name;
    for (const s of majorsOf(p)) {
      // 宮主星：稍低權重，避免盤面標籤淹没問題
      pushUnique(out, {
        term: s,
        reason: `${label}有${s}`,
        source: 'chart',
        weight: p.isMingGong ? 2.2 : 0.9,
      });
    }
    for (const s of p.stars) {
      if (!s.siHua) continue;
      const hua = `化${s.siHua}`;
      pushUnique(out, {
        term: hua,
        reason: `${label}${s.name}化${s.siHua}`,
        source: 'chart',
        weight: s.siHua === '忌' ? 1.8 : 1.5,
      });
      pushUnique(out, {
        term: s.name,
        reason: `${s.name}化${s.siHua}`,
        source: 'chart',
        weight: 1.4,
      });
    }
    if (p.selfSihua?.length) {
      for (const m of p.selfSihua) {
        pushUnique(out, {
          term: '自化',
          reason: `${label}自化${m.siHua}`,
          source: 'chart',
          weight: 1.3,
        });
        pushUnique(out, {
          term: `化${m.siHua}`,
          reason: `${label}自化${m.siHua}`,
          source: 'chart',
          weight: 1.2,
        });
      }
    }
  }

  const dx = chart.daXians[chart.currentDaXianIndex];
  if (dx) {
    const pn = dx.palaceName;
    const label = PALACE_LABEL[pn] ?? pn;
    pushUnique(out, { term: '大限', reason: `當前大限${label}`, source: 'chart', weight: 1.6 });
    pushUnique(out, {
      term: pn.endsWith('宫') ? pn : `${pn}`,
      reason: `大限在${label}`,
      source: 'chart',
      weight: 1.3,
    });
    if (dx.siHua) {
      for (const [k, star] of [
        ['禄', dx.siHua.lu],
        ['权', dx.siHua.quan],
        ['科', dx.siHua.ke],
        ['忌', dx.siHua.ji],
      ] as const) {
        if (!star) continue;
        pushUnique(out, {
          term: `化${k}`,
          reason: `大限化${k}（${star}）`,
          source: 'chart',
          weight: 1.2,
        });
        pushUnique(out, {
          term: star,
          reason: `大限${star}化${k}`,
          source: 'chart',
          weight: 1.0,
        });
      }
    }
  }

  if (chart.yinYangGender?.includes('女')) {
    pushUnique(out, { term: '女命', reason: '女命盤', source: 'chart', weight: 1.0 });
  } else if (chart.yinYangGender?.includes('男')) {
    pushUnique(out, { term: '男命', reason: '男命盤', source: 'chart', weight: 0.7 });
  }

  return out;
}

function fieldBoost(field: 'h2' | 'h3' | 'quote' | 'note' | 'meta'): number {
  switch (field) {
    case 'h2':
      return 3.2;
    case 'h3':
      return 2.4;
    case 'quote':
      return 1.0;
    case 'note':
      return 0.85;
    case 'meta':
      return 0.7;
  }
}

/** 對語料評分排序 */
export function rankQuotes(
  entries: NihaiEntry[],
  questionTerms: GuideTerm[],
  chartTerms: GuideTerm[],
  topN = 12,
): GuideHit[] {
  const terms = [...questionTerms, ...chartTerms];
  if (!terms.length) return [];

  // 問題優先：若同一 term 兩邊都有，保留較高權重並標記
  const byTerm = new Map<string, GuideTerm[]>();
  for (const t of terms) {
    const arr = byTerm.get(t.term) ?? [];
    arr.push(t);
    byTerm.set(t.term, arr);
  }

  const hits: GuideHit[] = [];

  for (const e of entries) {
    const h2 = e.h2 || '';
    const h3 = e.h3 || '';
    const quote = e.quote || '';
    const note = e.note || '';
    const meta = `${e.bookLabel}\n${e.lecture}\n${e.lectureTitle}`;

    let score = 0;
    const reasons: string[] = [];
    const matched: string[] = [];
    const reasonSet = new Set<string>();

    for (const [term, variants] of byTerm) {
      if (!term) continue;
      const best = variants.reduce((a, b) => (a.weight >= b.weight ? a : b));
      const qBoost = variants.some((v) => v.source === 'question') ? 1.35 : 1;
      const bothBoost = variants.some((v) => v.source === 'question') && variants.some((v) => v.source === 'chart') ? 1.25 : 1;

      let local = 0;
      let where: 'h2' | 'h3' | 'quote' | 'note' | 'meta' | null = null;
      if (h2.includes(term)) {
        local = fieldBoost('h2');
        where = 'h2';
      } else if (h3.includes(term)) {
        local = fieldBoost('h3');
        where = 'h3';
      } else if (quote.includes(term)) {
        local = fieldBoost('quote');
        where = 'quote';
      } else if (note.includes(term)) {
        local = fieldBoost('note');
        where = 'note';
      } else if (meta.includes(term)) {
        local = fieldBoost('meta');
        where = 'meta';
      }
      if (!where) continue;

      const add = local * best.weight * qBoost * bothBoost;
      score += add;
      matched.push(term);
      // 優先用問題因由，否則用盤面因由
      const reason =
        variants.find((v) => v.source === 'question')?.reason ??
        variants.find((v) => v.source === 'chart')?.reason ??
        `命中「${term}」`;
      if (!reasonSet.has(reason) && reasons.length < 4) {
        reasonSet.add(reason);
        reasons.push(reason);
      }
    }

    if (score > 0) {
      hits.push({ entry: e, score, reasons, matchedTerms: matched });
    }
  }

  hits.sort((a, b) => b.score - a.score || a.entry.i - b.entry.i);
  return hits.slice(0, topN);
}

export function guideSearch(
  entries: NihaiEntry[],
  question: string,
  chart: ZiweiChart | null,
  topN = 12,
): { hits: GuideHit[]; questionTerms: GuideTerm[]; chartTerms: GuideTerm[] } {
  const questionTerms = extractQuestionTerms(question);
  const chartTerms = chart ? extractChartTerms(chart) : [];
  const hits = rankQuotes(entries, questionTerms, chartTerms, topN);
  return { hits, questionTerms, chartTerms };
}

/** 畀「喺原話分頁睇」預填關鍵字：取問題主詞 */
export function keywordForNihaiTab(questionTerms: GuideTerm[], hits: GuideHit[]): string {
  const fromQ = questionTerms
    .filter((t) => t.weight >= 2)
    .map((t) => t.term)
    .slice(0, 3);
  if (fromQ.length) return fromQ.join(' ');
  const fromHits = hits[0]?.matchedTerms.slice(0, 2) ?? [];
  return fromHits.join(' ');
}
